/**
 * useS3Upload — the three-phase direct-to-S3 upload state machine, lifted
 * out of `<AvatarUploader>` and `<LogoUploader>` (second usage of the
 * pattern; third would be a smell — AGENTS.md §10 The Copy-Paste Helper).
 *
 * Handles one file or a batch: the input may be `multiple`, and `uploadFiles`
 * accepts an explicit list so a caller can feed it from a second input (e.g.
 * `webkitdirectory` folder picking) without re-implementing the sequence.
 * Single-file callers pass a 1-element list and behave exactly as before.
 *
 * Flow (per file, sequentially):
 *   1. Client validates picked file (mime + size).
 *   2. Calls a `requestUpload` mutation → presigned URL + objectKey.
 *   3. Direct PUT to the S3 URL with `Content-Type` matching what was
 *      signed; the API never sees the bytes.
 *   4. Calls a `confirmUpload` mutation with the objectKey → DB flips +
 *      audit row writes.
 *   5. Invalidates whichever caller-specified queries depend on the new
 *      object URL (so the topbar avatar / cover logo refresh in place).
 *
 * The hook returns:
 *   - `status` — observable state for the UI to render helper / progress / error.
 *   - `openPicker` — invoke from a button onClick.
 *   - `inputRef` — attach to a hidden `<input type="file">`.
 *   - `inputProps` — `accept`, `disabled`, `onChange` to spread on the input.
 *
 * The visual chip (avatar vs logo, sizes, fallback) stays per-component.
 * Only the orchestration is shared.
 */
import * as React from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";

export type S3UploadStatus =
  | { kind: "idle" }
  | {
      kind: "uploading";
      phase: "request" | "put" | "confirm";
      /** 1-based position in the batch; both omitted for a single file. */
      index?: number;
      total?: number;
    }
  | { kind: "saved" }
  | { kind: "error"; message: string };

/** Outcome of a batch — lets the caller report what it skipped and why. */
export interface S3UploadBatchResult {
  uploaded: number;
  /** Files rejected client-side (wrong type / too large), with the reason. */
  skipped: { name: string; reason: string }[];
  /** Files that failed mid-flight (presign / PUT / confirm), with the reason. */
  failed: { name: string; reason: string }[];
}

/**
 * How long the `saved` status persists before the hook flips back to
 * `idle`. Long enough for a user to notice the confirmation copy, short
 * enough that the helper line doesn't linger stale after they've moved on.
 */
const SAVED_MESSAGE_MS = 4000;

/**
 * Shape of the request mutation's result. Compatible with the wire output
 * of `identity.requestAvatarUpload` and `companies.requestLogoUpload`
 * (and any future request-* procedure that follows the same contract).
 */
export interface PresignedUpload {
  uploadUrl: string;
  objectKey: string;
}

/**
 * Caller-supplied bindings to the trpc layer. We accept the mutation
 * objects directly (rather than building them inside) so callers can
 * pass any future request/confirm pair without changing the hook.
 */
export interface UseS3UploadOptions {
  /** The presigned-URL request mutation (`.mutateAsync` is what we call). */
  request: {
    mutateAsync: (input: { contentType: string; size: number }) => Promise<PresignedUpload>;
  };
  /** The confirm mutation. */
  confirm: {
    mutateAsync: (input: { objectKey: string }) => Promise<unknown>;
  };
  /**
   * Allowed MIME types. Client-side check only — the server's wire schema
   * is the floor (this saves a wire round-trip on bad files).
   */
  allowedTypes: ReadonlyArray<string>;
  /**
   * Resolve the canonical content type for a file, or null to reject it.
   * Browsers report `File.type` from OS/registry mappings, which are empty
   * or non-canonical for perfectly valid files (a folder pick of `.png`s on
   * Windows can yield `""`); a resolver can fall back to the extension. The
   * resolved value is used for the pre-flight check, the presign request AND
   * the PUT `Content-Type` header. Default: `file.type` when it's in
   * `allowedTypes`, else reject.
   */
  resolveType?: (file: File) => string | null;
  /** Max bytes allowed. Same client-floor / server-floor split. */
  maxBytes: number;
  /**
   * Query keys to invalidate on confirm-success. Pass the keys for any
   * cached data that exposes the uploaded object's URL — typically
   * `['identity', 'me']` for avatars, the company's get key for logos.
   */
  invalidateQueries: ReadonlyArray<QueryKey>;
  /** Optional: copy hints for the error messages. Helpful for "Image must be under 1 MB" vs "2 MB". */
  copy?: {
    invalidType?: string;
    tooLarge?: string;
    putFailed?: (status: number, body: string) => string;
  };
  /** Notified on every state change. Caller renders the helper line. */
  onStatusChange?: (status: S3UploadStatus) => void;
  /**
   * Called after each file's PUT + confirm + cache invalidation succeeds — so
   * a batch fires it once per file, letting the caller append as it goes.
   */
  onSuccess?: () => void | Promise<void>;
  /** Allow picking more than one file. Default false (single-file callers). */
  multiple?: boolean;
}

export interface UseS3UploadResult {
  status: S3UploadStatus;
  /** True while the state machine is in any uploading phase. */
  isBusy: boolean;
  /** Click handler for the visible upload affordance. */
  openPicker: () => void;
  /** Attach to a hidden `<input type="file">`. */
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** Spread on the file input. */
  inputProps: {
    type: "file";
    accept: string;
    disabled: boolean;
    multiple?: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  };
  /**
   * Upload an explicit list, bypassing this hook's own input. Lets a caller
   * feed files from anywhere — a second `webkitdirectory` input, a drop zone —
   * without re-implementing the request → PUT → confirm sequence.
   */
  uploadFiles: (files: File[]) => Promise<S3UploadBatchResult>;
}

const DEFAULT_COPY: Required<NonNullable<UseS3UploadOptions["copy"]>> = {
  invalidType: "Use one of the allowed file types.",
  tooLarge: "File is larger than the allowed maximum.",
  putFailed: (status, body) => `Upload failed (${status}). ${body.slice(0, 200)}`,
};

export function useS3Upload(options: UseS3UploadOptions): UseS3UploadResult {
  const { request, confirm, allowedTypes, maxBytes, invalidateQueries } = options;
  const copy = { ...DEFAULT_COPY, ...options.copy };

  const inputRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<S3UploadStatus>({ kind: "idle" });

  // Bubble status to the host without forcing them to track their own copy.
  React.useEffect(() => {
    options.onStatusChange?.(status);
  }, [status, options]);

  const isBusy = status.kind === "uploading";

  // Auto-revert `saved` → `idle` so the helper line doesn't linger stale.
  // Guard against races (a fresh upload starting inside the timeout) via
  // the functional updater: only flip if we're still in the same saved
  // state we scheduled the timeout for.
  React.useEffect(() => {
    if (status.kind !== "saved") return;
    const t = setTimeout(() => {
      setStatus((current) => (current.kind === "saved" ? { kind: "idle" } : current));
    }, SAVED_MESSAGE_MS);
    return () => clearTimeout(t);
  }, [status]);

  const openPicker = React.useCallback(() => {
    if (status.kind === "uploading") return;
    inputRef.current?.click();
  }, [status.kind]);

  /**
   * The batch is sequential on purpose: a folder of 8 images shouldn't fire 8
   * concurrent PUTs, and one bad file shouldn't abandon the rest — pre-flight
   * rejects go to `skipped`, mid-flight errors go to `failed`, and the loop
   * carries on with the remaining files either way.
   */
  const uploadFiles = React.useCallback(
    async (files: File[]): Promise<S3UploadBatchResult> => {
      const resolveType =
        options.resolveType ??
        ((file: File) => (allowedTypes.includes(file.type) ? file.type : null));
      const skipped: { name: string; reason: string }[] = [];
      const accepted: { file: File; contentType: string }[] = [];
      for (const file of files) {
        const contentType = resolveType(file);
        if (!contentType) {
          skipped.push({ name: file.name, reason: copy.invalidType });
        } else if (file.size > maxBytes) {
          skipped.push({ name: file.name, reason: copy.tooLarge });
        } else {
          accepted.push({ file, contentType });
        }
      }
      // Nothing usable: surface the first reason rather than a silent no-op.
      if (accepted.length === 0) {
        const first = skipped[0];
        if (first) setStatus({ kind: "error", message: first.reason });
        return { uploaded: 0, skipped, failed: [] };
      }

      const total = accepted.length;
      // Position/total only when there's a batch to count through — single-file
      // callers keep the exact status shape they had before.
      const progress = (phase: "request" | "put" | "confirm", i: number): S3UploadStatus =>
        total > 1
          ? { kind: "uploading", phase, index: i + 1, total }
          : { kind: "uploading", phase };

      let uploaded = 0;
      const failed: { name: string; reason: string }[] = [];
      for (const [i, { file, contentType }] of accepted.entries()) {
        try {
          setStatus(progress("request", i));
          const presigned = await request.mutateAsync({
            contentType,
            size: file.size,
          });

          setStatus(progress("put", i));
          // Direct PUT to S3 with the resolved Content-Type — it must match
          // what was signed; the SDK pinned that header into the URL, S3
          // enforces it server-side. No `credentials: 'include'` — S3 isn't
          // on our origin and the signature itself authenticates the request.
          const putRes = await fetch(presigned.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": contentType },
            body: file,
          });
          if (!putRes.ok) {
            const body = await putRes.text().catch(() => "");
            throw new Error(copy.putFailed(putRes.status, body));
          }

          setStatus(progress("confirm", i));
          await confirm.mutateAsync({ objectKey: presigned.objectKey });

          // Refetch so any UI that reads the new URL refreshes in-place
          // (no flicker to initials).
          await Promise.all(
            invalidateQueries.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
          );
          // Per file, so a caller appending to a list gets each one as it lands.
          await options.onSuccess?.();
          uploaded += 1;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Something went wrong. Please try again.";
          failed.push({ name: file.name, reason: message });
        }
      }

      if (failed.length === 0) {
        // Flash a `saved` state so the caller can render "Saved just now"
        // copy — users don't otherwise know the upload persisted (the file
        // was PUT and the DB flipped, but the SaveBar / any other visible
        // form controls stay unchanged because the object key isn't part
        // of the form's dirty-tracked shape).
        setStatus({ kind: "saved" });
      } else {
        const first = failed[0]!;
        setStatus({
          kind: "error",
          message:
            failed.length === 1 && total === 1
              ? first.reason
              : `${failed.length} of ${total} uploads failed. ${first.reason}`,
        });
      }
      return { uploaded, skipped, failed };
    },
    [allowedTypes, maxBytes, request, confirm, invalidateQueries, queryClient, copy, options],
  );

  const onChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      // Reset the input so re-picking the same file (after a failure) refires
      // change. Without this, the second selection is a silent no-op.
      if (event.target) event.target.value = "";
      if (files.length === 0) return;
      await uploadFiles(files);
    },
    [uploadFiles],
  );

  return {
    status,
    isBusy,
    openPicker,
    inputRef,
    inputProps: {
      type: "file",
      accept: allowedTypes.join(","),
      disabled: isBusy,
      ...(options.multiple ? { multiple: true } : {}),
      onChange,
    },
    uploadFiles,
  };
}
